import { useTenantID } from '@/hooks/useTenantID';
import { useMemo, ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { cn } from '@/helpers/classnames';
import { useTheme } from '@/stores/theme';

// LTN, Länstrafiken Norrland
import ltnLogo from '@/assets/branding/ltn-logo.svg';
import ltnLogoDark from '@/assets/branding/ltn-logo-dark.png';
import ltnWelcome from '@/assets/branding/ltn-welcome.png';

// LTR, Länstrafiken Jämtland
import ltrLogo from '@/assets/branding/ltr-logo.svg';
import ltrLogoDark from '@/assets/branding/ltr-logo-dark.png';
import ltrWelcome from '@/assets/branding/ltr-welcome.png';

// LLT, Luleå Lokaltrafik
import lltLogo from '@/assets/branding/llt-logo.png';
import lltLogoDark from '@/assets/branding/llt-logo-dark.png';
import lltWelcome from '@/assets/branding/llt-welcome.png';

// LTV, Länstrafiken Västerbotten
import ltvLogo from '@/assets/branding/ltv-logo.svg';
import ltvLogoDark from '@/assets/branding/ltv-logo-dark.png';
import ltvWelcome from '@/assets/branding/ltv-welcome.png';

// VLT, Din tur / Länstrafiken Västernorrland
import vltLogo from '@/assets/branding/vlt-logo.svg';
import vltLogoDark from '@/assets/branding/vlt-logo-dark.png';
import vltWelcome from '@/assets/branding/vlt-welcome.png';

// SKE, Skellefteåbuss
import skeLogo from '@/assets/branding/ske-logo.png';
import skeLogoDark from '@/assets/branding/ske-logo-dark.png';

// DEMO
import demoLogo from '@/assets/branding/demo-logo.png';
import demoWelcome from '@/assets/branding/demo-welcome.png';

export const TENANTS: Record<string, string> = {
  ltn: 'Länstrafiken Norrbotten',
  ltr: 'Länstrafiken Jämtland',
  llt: 'Luleå Lokaltrafik',
  ltv: 'Länstrafiken Västerbotten',
  vlt: 'Din tur',
  ske: 'Skellefteå buss',
  demo: 'Demo',
};

export const useBranding = (): {
  logo: ReactNode;
  welcome: string;
  name: string;
  serviceName: string;
  tagLine: string;
} => {
  const tenantId = useTenantID({ dontThrow: true });
  const { formatMessage } = useIntl();
  const { isDark } = useTheme();

  const data = useMemo(() => {
    const tenantName = TENANTS[tenantId] || tenantId;

    const altText = formatMessage(
      {
        defaultMessage: '{name} logga',
      },
      { name: tenantName },
    );

    const tagLine = 'Biljetter. Resor. Enkelt.';

    const baseClassName = 'm-auto h-auto max-h-full object-cover';

    switch (tenantId) {
      case 'ltn':
        return {
          logo: (
            <img
              src={isDark ? ltnLogoDark : ltnLogo}
              alt={altText}
              className={cn(baseClassName, 'p-1 dark:p-0')}
            />
          ),
          welcome: ltnWelcome,
          name: tenantName,
          serviceName: 'Reseportalen för skola och företag',
          tagLine,
        };

      case 'ltr':
        return {
          logo: (
            <img
              src={isDark ? ltrLogoDark : ltrLogo}
              alt={altText}
              className={cn(baseClassName, 'p-3')}
            />
          ),
          welcome: ltrWelcome,
          name: tenantName,
          serviceName: 'Länstrafikens Partnerportal',
          tagLine:
            'Ett enklare resande för företag, kommuner och andra organisationer',
        };

      case 'llt':
        return {
          logo: (
            <img
              src={isDark ? lltLogoDark : lltLogo}
              alt={altText}
              className={cn(baseClassName, 'p-3')}
            />
          ),
          welcome: lltWelcome,
          name: tenantName,
          serviceName: 'LLT Företag & Skola',
          tagLine,
        };

      case 'ltv':
        return {
          logo: (
            <img
              src={isDark ? ltvLogoDark : ltvLogo}
              alt={altText}
              className={cn(baseClassName, 'p-2')}
            />
          ),
          welcome: ltvWelcome,
          name: tenantName,
          serviceName: 'Länstrafiken Företag & Skola',
          tagLine,
        };

      case 'vlt':
        return {
          logo: (
            <img
              src={isDark ? vltLogoDark : vltLogo}
              alt={altText}
              className={cn(baseClassName, 'p-2')}
            />
          ),
          welcome: vltWelcome,
          name: tenantName,
          serviceName: 'Din Tur Företag',
          tagLine,
        };

      case 'ske':
        return {
          logo: (
            <img
              src={isDark ? skeLogoDark : skeLogo}
              alt={altText}
              className={cn(baseClassName, 'p-3')}
            />
          ),
          welcome: ltrWelcome,
          name: tenantName,
          serviceName: 'AllTix',
          tagLine,
        };

      case 'demo':
        return {
          logo: (
            <img
              src={demoLogo}
              alt={altText}
              className={cn(baseClassName, 'p-3')}
            />
          ),
          welcome: demoWelcome,
          name: tenantName,
          serviceName: 'AllTix',
          tagLine,
        };

      default:
        return {
          logo: <div>Logo missing</div>,
          welcome: '-',
          name: tenantName,
          serviceName: 'AllTix',
          tagLine,
        };
    }
  }, [tenantId, formatMessage, isDark]);

  return data;
};
